import {
  buildCollection,
  buildEntityCallbacks,
  buildProperties,
  buildProperty,
  EntityReference,
  type EntityStatus,
  type MapProperty,
} from "@firecms/core";
import { buildAccountPermissions } from "../utils";
import { type LocalizedString } from "../localization";
import {
  codeProperty,
  feedbackCorrectProperty,
  feedbackIncorrectProperty,
  questionHintProperty,
  questionOptionTextProperty,
  questionProperty,
} from "../properties/common";
import { buildUnitsPath } from "./units";
import {
  getContentBasePath,
  type ContentScopeId,
} from "../workspace/collectionIds";
import {
  onPreSaveContentTimestamps,
  type ContentTimestamps,
} from "./contentTimestamps";
import { assignOptionCodes } from "../contentImport/assignOptionCodes";
import { buildContentImportActions } from "../contentImport/buildContentImportActions";
import { validateQuestionOptions } from "../contentImport/validateQuestionOptions";

export const QuestionDifficulty = {
  QUESTION_DIFFICULTY_EASY: "QUESTION_DIFFICULTY_EASY",
  QUESTION_DIFFICULTY_MEDIUM: "QUESTION_DIFFICULTY_MEDIUM",
  QUESTION_DIFFICULTY_HARD: "QUESTION_DIFFICULTY_HARD",
} as const;

export type QuestionDifficulty =
  (typeof QuestionDifficulty)[keyof typeof QuestionDifficulty];

export type QuestionOption = {
  code: string;
  text: LocalizedString;
  isCorrect: boolean;
};

export type Question = ContentTimestamps & {
  code: string;
  unitRef: EntityReference;
  question: LocalizedString;
  hint?: LocalizedString;
  feedbackCorrect: LocalizedString;
  feedbackIncorrect: LocalizedString;
  options: QuestionOption[];
  difficulty: QuestionDifficulty;
};

const questionDifficultyEnumValues = {
  [QuestionDifficulty.QUESTION_DIFFICULTY_EASY]: "Easy",
  [QuestionDifficulty.QUESTION_DIFFICULTY_MEDIUM]: "Medium",
  [QuestionDifficulty.QUESTION_DIFFICULTY_HARD]: "Hard",
};

const questionOptionPropertyParams: MapProperty<QuestionOption> = {
  name: "Option",
  validation: { required: true },
  dataType: "map",
  properties: {
    code: buildProperty({
      name: "Code",
      description: "Assigned automatically on save. Stable identity for answers and logs.",
      dataType: "string",
      readOnly: true,
    }),
    text: questionOptionTextProperty,
    isCorrect: buildProperty({
      name: "Correct",
      description: "Mark if this option is a valid answer (one or more per question).",
      validation: { required: true },
      dataType: "boolean",
      defaultValue: false,
    }),
  },
};

export const onPreSaveQuestion = ({
  values,
  status,
}: {
  values: Partial<Question>;
  status: EntityStatus;
}) => {
  onPreSaveContentTimestamps({ values, status });

  values.options = assignOptionCodes(values.options ?? []);
  const validation = validateQuestionOptions(values.options);
  if (!validation.ok) {
    throw new Error(validation.reason);
  }

  return values;
};

export const buildQuestionsPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/questions`;

export const buildQuestionsCollection = (id: ContentScopeId) =>
  buildCollection<Question>({
    name: "Questions",
    singularName: "Question",
    defaultSize: "s",
    description:
      "Reusable multiple-choice items for a unit. Levels can draw from this pool instead of embedding questions.",
    path: buildQuestionsPath(id),
    id: "questions",
    icon: "HelpOutline",
    initialSort: ["code", "asc"],
    group: "Learn",
    callbacks: buildEntityCallbacks({
      onPreSave: onPreSaveQuestion,
    }),
    permissions: buildAccountPermissions(id),
    properties: buildProperties<Question>({
      code: codeProperty,
      unitRef: buildProperty({
        name: "Unit",
        validation: { required: true },
        dataType: "reference",
        path: buildUnitsPath(id),
        previewProperties: [],
        includeId: false,
        includeEntityLink: false,
      }),
      question: questionProperty,
      hint: questionHintProperty,
      feedbackCorrect: feedbackCorrectProperty,
      feedbackIncorrect: feedbackIncorrectProperty,
      options: buildProperty({
        name: "Options",
        validation: { required: true },
        dataType: "array",
        expanded: false,
        of: buildProperty<QuestionOption>(questionOptionPropertyParams),
      }),
      difficulty: buildProperty({
        name: "Difficulty",
        validation: { required: true },
        dataType: "string",
        enumValues: questionDifficultyEnumValues,
        defaultValue: QuestionDifficulty.QUESTION_DIFFICULTY_MEDIUM,
      }),
      createdAt: buildProperty({
        name: "Created",
        dataType: "date",
        readOnly: true,
        autoValue: "on_create",
        hideFromCollection: true,
      }),
      updatedAt: buildProperty({
        name: "Updated",
        dataType: "date",
        readOnly: true,
        autoValue: "on_update",
        hideFromCollection: true,
      }),
    }),
    Actions: buildContentImportActions("questions"),
  });
